import TableLeaderboard from './TableLeaderboard';
import ShareActions from './ShareActions';
import type { ScoreChallenge } from './share';
import type { LocalScore } from '../ui/localLeaderboard';
import styles from './SelectedTableScores.module.css';

interface SelectedTableScoresProps {
  tableId: string;
  tableName: string;
  localScores: LocalScore[];
  online: boolean;
  challenge?: ScoreChallenge | null;
  challenger?: string | null;
  refreshKey?: number;
}

export default function SelectedTableScores({
  tableId,
  tableName,
  localScores,
  online,
  challenge,
  challenger,
  refreshKey = 0,
}: SelectedTableScoresProps) {
  const localTop = localScores
    .filter((entry) => entry.tableId === tableId)
    .sort((left, right) => right.score - left.score || left.createdAt - right.createdAt)
    .slice(0, 5);

  return (
    <section className={styles.panel} aria-labelledby="selected-table-scores-title">
      <div className={styles.header}>
        <div>
          <span>SELECTED MACHINE</span>
          <h2 id="selected-table-scores-title">High scores · {tableName}</h2>
        </div>
        {online && <ShareActions tableId={tableId} tableName={tableName} challenger={challenger} compact />}
      </div>

      {challenge?.tableId === tableId && (
        <div className={styles.challenge} role="status">
          <span>{challenge.challenger ? `CHALLENGE FROM ${challenge.challenger.toUpperCase()}` : 'FRIEND CHALLENGE'}</span>
          <strong>Beat {challenge.score.toLocaleString()}</strong>
          <small>Play this exact machine and finish above the target score.</small>
        </div>
      )}

      <div className={styles.boards}>
        <section className={styles.local} aria-label={`${tableName} scores on this device`}>
          <div className={styles.boardTitle}>
            <strong>This device</strong>
            <span>Personal arcade</span>
          </div>
          {localTop.length ? (
            <ol>
              {localTop.map((entry, index) => (
                <li key={`${entry.createdAt}-${entry.initials}`}>
                  <b>{index + 1}</b>
                  <span>{entry.initials}</span>
                  <strong>{entry.score.toLocaleString()}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <p>No saved scores yet. Claim the first spot.</p>
          )}
        </section>

        <section className={styles.online} aria-label={`${tableName} online leaderboard`}>
          <div className={styles.boardTitle}>
            <strong>Online</strong>
            <span>{online ? 'Best signed-in runs' : 'Publish to unlock'}</span>
          </div>
          {online ? (
            <TableLeaderboard
              tableId={tableId}
              defaultOpen
              refreshKey={refreshKey}
              limit={5}
              showToggle={false}
            />
          ) : (
            <p>Online scores and portable share links unlock when this draft is published.</p>
          )}
        </section>
      </div>
    </section>
  );
}
